import { useNavigate } from 'react-router-dom';
import { IdCard, LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { UserAvatar } from '@/components/UserAvatar';
import { NotificationsPopover } from '@/components/public/NotificationsPopover';
import { useAuthStore } from '@/store/auth';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { hasAnyRole } from '@court-sync/shared';

/**
 * Authenticated student/guardian/member controls for the public org nav bar.
 */
export function PublicOrgConsumerMenu() {
  const { user, logout } = useAuthStore();
  const { p } = useOrgPaths();
  const consumerNav = useUnifiedConsumerNav();
  const navigate = useNavigate();

  if (!user || !p) return null;

  const isConsumer = hasAnyRole(user, ['guardian', 'student', 'member']);
  if (!isConsumer) return null;

  const settingsPath = p('/settings');
  const membershipPath = p('/membership');
  const notificationsPath = p('/notifications');

  const handleSignOut = async () => {
    await logout();
    navigate(p(''), { replace: true });
  };

  return (
    <div className="flex shrink-0 items-center gap-1">
      <NotificationsPopover userId={user.id} viewAllPath={notificationsPath} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 gap-2 px-2" aria-label="Account menu">
            <UserAvatar
              avatarUrl={user.avatarUrl}
              firstName={user.firstName}
              lastName={user.lastName}
              size="sm"
            />
            <span className="hidden max-w-[8rem] truncate text-sm text-muted-foreground lg:inline">
              {user.firstName} {user.lastName}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          <DropdownMenuLabel>
            {user.firstName} {user.lastName}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(settingsPath)}>
            <Settings className="mr-2 h-4 w-4" />
            Account settings
          </DropdownMenuItem>
          {consumerNav.hasMemberships ? (
            <DropdownMenuItem onClick={() => navigate(membershipPath)}>
              <IdCard className="mr-2 h-4 w-4" />
              Membership
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => void handleSignOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
